import { getLocalApiOrigin, resolveLocalImageUrl } from '../localApi';
import { sorceryImageUrl, corsTextureUrl } from '../cardImageUrl';
import { isWeb } from '../platform';
import { resolvePrinting, isFoilFinish } from '../sorcery/foil';
import { foilMapUrl, depthMapUrl } from '../sorcery/foilMaterial';

const VALID_SLUG = /^[a-z0-9_-]+$/i;

/**
 * Resolve every printing a deck will actually render at the table:
 * the EXACT printing each entry resolves to (same resolvePrinting
 * call the game uses, so foil printings with their own art are
 * included), every standard printing as fallback coverage, and any
 * printed back-face.
 */
function extractDeckPrintings(deck, sorceryCards) {
  const printings = new Map(); // slug -> printing
  if (!deck || !Array.isArray(deck.cards) || !Array.isArray(sorceryCards)) return printings;
  const catalog = new Map();
  for (const c of sorceryCards) catalog.set(c.unique_id, c);
  const addPrinting = (printing) => {
    const slug = printing?.id;
    if (typeof slug === 'string' && VALID_SLUG.test(slug) && !printings.has(slug)) {
      printings.set(slug, printing);
    }
  };
  for (const entry of deck.cards) {
    const card = catalog.get(entry?.cardId);
    if (!card) continue;
    // Mirror gameState/authZones: this is the printing whose image_url
    // the hand + board will request (incl. ?cb= on foil slugs).
    addPrinting(resolvePrinting(card, entry?.printingId, entry?.foiling ?? 'S') || card.printings?.[0]);
    for (const p of (card.printings || [])) {
      if (p.foiling === 'S') addPrinting(p);
    }
    if (card.backFace) {
      for (const p of (card.backFace.printings || [])) addPrinting(p);
    }
  }
  return printings;
}

/**
 * Verify every slug is on disk; trigger a blocking download for any
 * that aren't. Resolves only once the download status reports `done`.
 * Throws if the local server returns a non-2xx. The gate surfaces a
 * retry button rather than letting the user enter a match with a
 * half-downloaded deck.
 */
async function ensureSlugsOnDisk(slugSet, onProgress) {
  if (slugSet.size === 0) return;
  // Web has no disk cache; phase 2's prefetchAsset warm-up below fills
  // the browser HTTP cache straight from the CDN instead.
  if (isWeb()) return;
  const api = getLocalApiOrigin();
  const slugs = [...slugSet];

  const missingRes = await fetch(`${api}/api/assets/check-missing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slugs }),
  });
  if (!missingRes.ok) throw new Error(`asset check failed (HTTP ${missingRes.status})`);
  const { missing } = await missingRes.json();
  if (!Array.isArray(missing) || missing.length === 0) return;

  onProgress?.({ phase: 'fetching', downloaded: 0, total: missing.length });

  const downloadRes = await fetch(`${api}/api/assets/download`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slugs: missing }),
  });
  if (!downloadRes.ok) throw new Error(`asset download failed (HTTP ${downloadRes.status})`);
  const initial = await downloadRes.json();
  if (initial.status === 'complete') {
    onProgress?.({ phase: 'fetching', downloaded: missing.length, total: missing.length });
    return;
  }

  // Poll until done. Cap the wait. A stalled batch shouldn't trap
  // the gate forever. 60s comfortably covers a fresh-install delta
  // (~1100 images) on a typical home connection; a true network outage
  // throws and surfaces the retry button.
  const deadline = Date.now() + 60_000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 300));
    const statusRes = await fetch(`${api}/api/assets/status`);
    if (!statusRes.ok) continue;
    const status = await statusRes.json();
    onProgress?.({
      phase: 'fetching',
      downloaded: status.downloaded || 0,
      total: missing.length,
    });
    if (status.done) return;
  }
  throw new Error('asset download timed out');
}

/**
 * Warm the browser HTTP cache for a single URL. THREE's texture loader
 * and the Audio element both read from that cache, so a primed cache
 * makes their later fetches a no-op. Body is consumed in full so the
 * cache entry is complete. Fetch() can return its promise before the
 * stream has drained. Best-effort; a single asset failing doesn't
 * abort the gate, because the runtime can still produce a placeholder.
 */
async function prefetchAsset(url) {
  if (!url) return;
  try {
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) return;
    const reader = res.body?.getReader();
    if (!reader) {
      await res.arrayBuffer();
      return;
    }
    // Drain the stream chunk by chunk, dropping each one. The HTTP cache
    // fills as the body streams, so the warm-up completes without ever
    // holding the asset's bytes in the JS heap (arrayBuffer() did. ~100
    // decks' worth of concurrent buffers spiked right at match entry).
    while (!(await reader.read()).done) { /* discard */ }
  } catch { /* best-effort */ }
}

// Warm the cache entry a plain DOM <img> will hit. On web the CDN
// replies with `Vary: Origin`, and a fetch() sends an Origin header
// while a plain <img> does not, so a fetch()-warmed entry is a cache
// MISS for the hand's <img> tags and the art re-downloads mid-match.
// An Image() warm issues the byte-identical no-Origin request (and
// pre-decodes the bitmap, so the hand paints instantly). decode()
// resolves after load+decode and rejects on failure; best-effort.
async function prefetchDomImage(url) {
  if (!url) return;
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
  } catch { /* best-effort */ }
}

// Bounded-concurrency warm-up: a handful of parallel streams saturate the
// connection just as well as one-fetch-per-asset, without the request and
// buffer burst. Items are `{ url, mode }`: 'image' warms via a DOM
// Image() (no Origin header), 'fetch' via fetch() (CORS mode, matches
// THREE texture loads and same-origin assets).
async function prefetchAll(items, concurrency = 6) {
  const queue = [...items];
  const worker = async () => {
    while (queue.length) {
      const item = queue.shift();
      if (item.mode === 'image') await prefetchDomImage(item.url);
      else await prefetchAsset(item.url);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
}

/**
 * Resolve every asset the GameBoard will reach for in its first
 * frame. Two phases:
 *   1. Verify card art is on disk (downloading any gaps).
 *   2. Warm the HTTP cache for cardbacks, playmat, table backdrop,
 *      match music, and every preloaded card slug.
 *
 * Callers pass `matchMusicUrl: null` when music is already playing
 * (custom-music suppressor, ambient-only, or the hub track that
 * carries over without re-fetching).
 */
export async function preloadMatchAssets({
  deck,
  sorceryCards,
  playmatUrl,
  matchMusicUrl,
  onProgress,
}) {
  const api = getLocalApiOrigin();

  const printings = extractDeckPrintings(deck, sorceryCards);
  onProgress?.({ phase: 'verifying', downloaded: 0, total: printings.size });
  await ensureSlugsOnDisk(new Set(printings.keys()), onProgress);

  onProgress?.({ phase: 'decoding', downloaded: 0, total: 0 });

  const items = [];
  const seen = new Set();
  const push = (url, mode) => {
    if (!url) return;
    const key = `${mode}:${url}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ url, mode });
  };
  // On desktop everything is same-origin (no Vary: Origin ambiguity),
  // so a single fetch() warm covers <img> and texture loads alike.
  const domMode = isWeb() ? 'image' : 'fetch';

  push(`${api}/game-assets/cardback-spellbook-rounded.png`, 'fetch');
  push(`${api}/game-assets/cardback-atlas-rounded.png`, 'fetch');
  push(`${api}/game-assets/table-background-hd.png`, 'fetch');
  if (typeof playmatUrl === 'string') {
    push(playmatUrl, 'fetch');
  } else if (playmatUrl && typeof playmatUrl === 'object') {
    push(playmatUrl.art, 'fetch');
    push(playmatUrl.ui, 'fetch');
  }
  if (matchMusicUrl) push(matchMusicUrl, 'fetch');

  for (const [slug, printing] of printings) {
    // The EXACT url the hand/dialog <img>s request (incl. any ?cb=
    // version tag on foil slugs), and the ?tex=1 variant the 3D board
    // and FoilCanvas request with an Origin header. Two cache entries
    // on web (see corsTextureUrl); they dedupe to one on desktop.
    const url = resolveLocalImageUrl(printing.image_url) || sorceryImageUrl(slug, api);
    push(url, domMode);
    push(corsTextureUrl(url), 'fetch');
    if (isFoilFinish(printing.foiling)) {
      // Foil hand cards immediately mount a FoilCanvas, which pulls
      // the baked foil + depth maps. Best-effort: either may 404
      // (runtime-derived fallback exists) without failing the gate.
      push(corsTextureUrl(foilMapUrl(url)), 'fetch');
      push(corsTextureUrl(depthMapUrl(url)), 'fetch');
    }
  }

  await prefetchAll(items);
}

export const __testing = { extractDeckPrintings };
